import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, GraduationCap } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

// Aggiunge un discente a una specifica azienda dal backoffice admin
// (POST /admin/organizations/{id}/learners). Lo username del discente è il codice
// fiscale; la password la imposta l'admin, come nel flusso del referente.
const EMPTY = { first_name: "", last_name: "", fiscal_code: "", password: "", email: "", birth_date: "" };

export default function NewLearnerDialog({ org, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const open = !!org;

  useEffect(() => { if (org) setForm(EMPTY); }, [org]);

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    const cf = form.fiscal_code.trim().toUpperCase();
    if (!/^[A-Z0-9]{16}$/.test(cf)) {
      toast.error("Il codice fiscale è obbligatorio e deve essere di 16 caratteri.");
      return;
    }
    if (form.password.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri.");
      return;
    }
    if (!form.birth_date) {
      toast.error("La data di nascita è obbligatoria (serve sull'attestato).");
      return;
    }
    setSaving(true);
    try {
      const body = {
        fiscal_code: cf,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email.trim() || null,
        birth_date: form.birth_date,
      };
      await api.post(`/admin/organizations/${org.id}/learners`, body);
      toast.success("Discente aggiunto.");
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm">
        <DialogHeader>
          <DialogTitle className="text-brand-blue flex items-center gap-2"><GraduationCap size={18} /> Nuovo discente</DialogTitle>
          <DialogDescription>
            {org?.ragione_sociale ? `Azienda: ${org.ragione_sociale}. ` : ""}
            Il discente accede con il codice fiscale e la password impostata qui.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Nome" value={form.first_name} onChange={setF("first_name")} required />
            <Fld label="Cognome" value={form.last_name} onChange={setF("last_name")} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld
              label="Codice fiscale (username)"
              value={form.fiscal_code}
              onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })}
              required maxLength={16} placeholder="RSSMRA85M01H501U"
            />
            <Fld label="Password" type="text" value={form.password} onChange={setF("password")} required minLength={8} placeholder="min. 8 caratteri" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Email (facoltativa)" type="email" value={form.email} onChange={setF("email")} />
            <Fld label="Data di nascita" type="date" value={form.birth_date} onChange={setF("birth_date")} required />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-brand-muted hover:text-brand-ink">Annulla</button>
            <Button type="submit" className="btn-primary" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={16} /> : "Aggiungi discente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, ...props }) {
  return (
    <div>
      <Label className="text-sm text-brand-ink">{label}</Label>
      <Input {...props} className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue" />
    </div>
  );
}
